import { useAppDispatch, useAppSelector } from "@/app/hooks";
import AddPoint from "@/components/DrawingNav/AddPoint";
import Crosshair from "@/components/DrawingNav/Crosshair";
import Undo from "@/components/DrawingNav/Undo";
import { navigationMenuLayout } from "@/components/Menu/menuSlice";
import { isSearching, loadCountiesShapes, searchError } from "@/components/Search/searchSlice";
import { useAccount } from "@/hooks/useAccount";
import { createSavedItemGraphics } from "@/lib/drawing-utils";
import * as bufferOperator from "@arcgis/core/geometry/operators/bufferOperator";
import { Alert, Box, Button, Loader, Stack, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import BasemapToggle from "./components/BasemapToggle";
import CurrentCoordinates from "./components/CurrentCoordinates";
import GetLocation from "./components/GetLocation";
import LegendModal from "./components/LegendModal";
import ResetNorth from "./components/ResetNorth";
import { useDrawingPoints } from "./hooks/useDrawingPoints";
import { useLayerLegendInfos } from "./hooks/useLayerLegendInfos";
import { useMapSession } from "./hooks/useMapSession";
import { useHighlights, useParcelSelection } from "./hooks/useParcelSelection";
import { useSavedItemGraphics } from "./hooks/useSavedItemGraphics";
import { loadPQMap, mapError, mapLoading, pointLayer, polygonLayer, webMap, zoomToSavedItem } from "./pqMapSlice";

const PQMap: React.FC = () => {
    const dispatch = useAppDispatch();
    const { userId } = useAccount();
    const mapRef = useRef<HTMLDivElement>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [retryAttempt, setRetryAttempt] = useState(0);
    const currentWebMap = useAppSelector(webMap);
    const currentPolygonLayer = useAppSelector(polygonLayer);
    const currentPointLayer = useAppSelector(pointLayer);
    const isWebMapLoading = useAppSelector(mapLoading)
    const webMapError = useAppSelector(mapError);
    const searching = useAppSelector(isSearching)
    const currentSearchError = useAppSelector(searchError);
    const currentNavigationMenuLayout = useAppSelector(navigationMenuLayout);
    const currentZoomToSavedItem = useAppSelector(zoomToSavedItem);

    const highlights = useHighlights();
    const session = useMapSession(mapRef, {
        webMap: currentWebMap,
        polygonLayer: currentPolygonLayer,
        pointLayer: currentPointLayer,
        retryAttempt,
        onNotice: setNotice,
        beforeDispose: highlights.clear,
    });
    const { view, stopTracking } = session;
    useParcelSelection({ view, polygonLayerView: session.polygonLayerView, polygonLayer: currentPolygonLayer, pointLayer: currentPointLayer, highlights, stopTracking, onNotice: setNotice });
    useSavedItemGraphics(session.graphicsLayer, userId);
    useLayerLegendInfos(session.legend, setNotice);
    const drawing = useDrawingPoints(view);

    useEffect(() => {
        if (currentWebMap) return;
        const request = dispatch(loadPQMap());
        return () => { request.abort(); };
    }, [dispatch, currentWebMap, retryAttempt])

    useEffect(() => {
        dispatch(loadCountiesShapes());
    }, [dispatch]);

    useEffect(() => {
        if (!view || !currentZoomToSavedItem || currentZoomToSavedItem.ownerId !== userId) return;
        const target = createSavedItemGraphics(currentZoomToSavedItem).find((graphic) => graphic.symbol?.type !== 'text')?.geometry;
        if (!target) return;
        stopTracking();
        void view.goTo(bufferOperator.execute(target, 100)).catch((error: Error) => {
            if (error.name !== 'AbortError') setNotice('This saved location could not be shown. Please try again.');
        });
    }, [currentZoomToSavedItem, stopTracking, userId, view])

    const error = session.error ?? webMapError;

    return (
        <>
            {error && <Box role="alert" style={{ position: 'absolute', inset: '60px 0 65px', zIndex: 2, background: 'white' }}>
                <Stack h="100%" justify="center" align="center" p="lg">
                    <Text ta="center">{error}</Text>
                    <Button color="pq-red" onClick={() => setRetryAttempt((attempt) => attempt + 1)}>Retry</Button>
                </Stack>
            </Box>}
            {!error && (isWebMapLoading || session.loading || searching) &&
                <Box style={{ position: "absolute", top: 'var(--app-shell-header-height, 0px)', bottom: 'var(--app-shell-footer-height, 0px)', zIndex: '1', backgroundColor: 'rgba(134,142,150,0.5)' }} h="calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px))" w="100%">
                    <Stack justify="center" align="center" h="100%">
                        <Loader size={100} color='pq-red' />
                    </Stack>
                </Box>
            }

            <Box id="map-container" ref={mapRef} bg="pq-gray" style={{ height: '100%', minWidth: '100%' }} />
            {!error && (notice || currentSearchError) && (
                // Solid background: the default light variant is a 10% tint, unreadable over map imagery.
                <Alert
                    color="pq-red"
                    variant="outline"
                    title="Map notice"
                    withCloseButton={Boolean(notice)}
                    onClose={() => setNotice(null)}
                    bg="white"
                    radius="md"
                    style={{ position: 'absolute', top: 110, left: 10, right: 10, zIndex: 2, boxShadow: 'var(--mantine-shadow-md)' }}
                >
                    {notice ?? currentSearchError}
                </Alert>
            )}
            {currentNavigationMenuLayout === 'default' && (
                <>
                    <CurrentCoordinates coords={session.coords} loading={session.dataLoading} />
                    <ResetNorth rotation={session.rotation} onClick={() => { void view?.goTo({ rotation: 0 }).catch(() => undefined); }} />
                    <GetLocation isTracking={session.isTracking} onEnableTracking={session.startTracking} onDisableTracking={stopTracking} />
                    <BasemapToggle />
                </>
            )}
            {currentNavigationMenuLayout === 'drawing' && (
                <>
                    <CurrentCoordinates loading={session.dataLoading} />
                    <AddPoint onClick={drawing.addPoint} />
                    <Crosshair />
                    <Undo onClick={drawing.undo} />
                </>
            )}
            <LegendModal onNotice={setNotice} />
        </>
    )
}

export default PQMap;
